import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiPlus,
  FiUser,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";


/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_CUSTOMER_FORM = {
  customerName: "",
  mobile: "",
  email: "",
  companyName: "",
  address: "",
};


/* =========================================================
   COMPONENT
========================================================= */

const AddCustomerDialog = ({
  open,
  initialValues,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();


  /* =======================================================
     FORM
  ======================================================= */

  const [
    form,
    setForm,
  ] = useState({
    ...INITIAL_CUSTOMER_FORM,
  });


  /* =======================================================
     ERRORS
  ======================================================= */

  const [
    errors,
    setErrors,
  ] = useState({});


  /* =======================================================
     SUBMITTING
  ======================================================= */

  const [
    submitting,
    setSubmitting,
  ] = useState(false);


  /* =======================================================
     RESET WHEN OPENED
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      ...INITIAL_CUSTOMER_FORM,

      customerName:
        initialValues?.customerName ||
        "",

      mobile:
        String(
          initialValues?.mobile ||
          ""
        )
          .replace(/\D/g, "")
          .slice(0, 10),
    });

    setErrors({});

    setSubmitting(false);

  }, [
    open,
    initialValues,
  ]);


  /* =======================================================
     DO NOT RENDER
  ======================================================= */

  if (!open) {
    return null;
  }


  /* =======================================================
     CHANGE
  ======================================================= */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    let updatedValue =
      value;


    /* =====================================================
       MOBILE
    ===================================================== */

    if (
      name ===
      "mobile"
    ) {
      updatedValue =
        value
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            10
          );
    }


    setForm(
      (previous) => ({
        ...previous,

        [name]:
          updatedValue,
      })
    );


    setErrors(
      (previous) => ({
        ...previous,

        [name]:
          "",
      })
    );
  };


  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm =
    () => {
      const validationErrors =
        {};


      /* =====================================================
         CUSTOMER NAME
      ===================================================== */

      if (
        !String(
          form.customerName ||
          ""
        ).trim()
      ) {
        validationErrors.customerName =
          "Customer name is required.";
      }


      /* =====================================================
         MOBILE
      ===================================================== */

      const mobile =
        String(
          form.mobile ||
          ""
        ).trim();


      if (!mobile) {
        validationErrors.mobile =
          "Mobile number is required.";

      } else if (
        !/^[6-9]\d{9}$/.test(
          mobile
        )
      ) {
        validationErrors.mobile =
          "Enter a valid 10-digit mobile number.";
      }


      /* =====================================================
         EMAIL
      ===================================================== */

      const email =
        String(
          form.email ||
          ""
        ).trim();


      if (
        email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email
        )
      ) {
        validationErrors.email =
          "Enter a valid email address.";
      }


      setErrors(
        validationErrors
      );


      return (
        Object.keys(
          validationErrors
        ).length === 0
      );
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();


      /* =====================================================
         PREVENT DOUBLE SUBMIT
      ===================================================== */

      if (submitting) {
        return;
      }


      /* =====================================================
         VALIDATE
      ===================================================== */

      if (
        !validateForm()
      ) {
        return;
      }


      try {
        setSubmitting(
          true
        );


        /* ===================================================
           PAYLOAD
        =================================================== */

        const payload = {
          customerName:
            String(
              form.customerName ||
              ""
            ).trim(),

          mobile:
            String(
              form.mobile ||
              ""
            ).trim(),

          email:
            String(
              form.email ||
              ""
            )
              .trim()
              .toLowerCase(),

          companyName:
            String(
              form.companyName ||
              ""
            ).trim(),

          address:
            String(
              form.address ||
              ""
            ).trim(),

          status:
            "Active",
        };


        /* ===================================================
           CREATE CUSTOMER
        =================================================== */

        const response =
          await api.post(
            config.createCustomer,
            payload
          );


        /* ===================================================
           CHECK RESPONSE
        =================================================== */

        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
            "Unable to create customer."
          );
        }


        /* ===================================================
           CUSTOMER DATA

           Support different backend response structures.
        =================================================== */

        const createdCustomer =
          response?.data?.customer ||
          response?.data ||
          response?.customer ||
          null;


        if (
          !createdCustomer?._id
        ) {
          console.error(
            "Create customer response:",
            response
          );

          throw new Error(
            "Customer was created, but customer data was not returned by the API."
          );
        }


        /*
         * IMPORTANT:
         *
         * Close/select customer FIRST.
         *
         * Do not wait for SweetAlert before
         * calling onSuccess.
         */

        if (
          typeof onSuccess ===
          "function"
        ) {
          onSuccess(
            createdCustomer
          );
        }


        /*
         * The parent AddEditRequirement will now:
         *
         * 1. Select this customer
         * 2. Update form.customer
         * 3. Close AddCustomerDialog
         *
         * Use SUCCESS notification here.
         * Do NOT use swal.confirm().
         */

        await swal.success({
          icon:
            "success",

          title:
            "Customer Added",

          text:
            response?.message ||
            "Customer created successfully.",

          timer:
            1400,

          showConfirmButton:
            false,
        });

      } catch (
        requestError
      ) {
        console.error(
          "Create customer error:",
          requestError
        );


        /* ===================================================
           EXISTING CUSTOMER

           Support both thrown API error structure and
           normal response style where possible.
        =================================================== */

        const existingCustomer =
          requestError
            ?.response
            ?.data
            ?.data
            ?.customer ||
          requestError
            ?.response
            ?.data
            ?.data ||
          null;


        const statusCode =
          Number(
            requestError
              ?.response
              ?.status ||
            requestError
              ?.response
              ?.data
              ?.code ||
            0
          );


        if (
          statusCode ===
            409 &&
          existingCustomer?._id
        ) {
          const confirmation =
            await swal.confirm({
              icon:
                "info",

              title:
                "Customer Already Exists",

              text:
                requestError
                  ?.response
                  ?.data
                  ?.message ||
                "A customer with this mobile number already exists.",

              showCancelButton:
                true,

              confirmButtonText:
                "Select Existing Customer",

              cancelButtonText:
                "Cancel",
            });


          if (
            confirmation
              ?.isConfirmed &&
            typeof onSuccess ===
              "function"
          ) {
            onSuccess(
              existingCustomer
            );
          }


          return;
        }


        /* ===================================================
           ERROR
        =================================================== */

        await swal.error({
          icon:
            "error",

          title:
            "Unable to add customer",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to create customer.",
        });

      } finally {
        /*
         * This always restores button state,
         * including API/server failures.
         */

        setSubmitting(
          false
        );
      }
    };


  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose =
    () => {
      if (submitting) {
        return;
      }

      if (
        typeof onClose ===
        "function"
      ) {
        onClose();
      }
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="add-customer-overlay"
    >
      <div
        className="add-customer-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Add new customer"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="add-customer-header"
        >
          <div>
            <span>
              CUSTOMER MANAGEMENT
            </span>

            <h2>
              Add New Customer
            </h2>
          </div>


          <button
            type="button"
            className="add-customer-close"
            onClick={
              handleClose
            }
            disabled={
              submitting
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="add-customer-body"
          >
            {/* =============================================
                NOTICE
            ============================================= */}

            <div
              className="add-customer-notice"
            >
              <FiUser />

              <div>
                <strong>
                  Create customer first
                </strong>

                <span>
                  After creating the customer,
                  it will be selected automatically
                  in the requirement form.
                </span>
              </div>
            </div>


            {/* =============================================
                GRID
            ============================================= */}

            <div
              className="add-customer-grid"
            >
              {/* CUSTOMER NAME */}

              <div
                className="add-customer-field"
              >
                <label>
                  Customer Name *
                </label>

                <input
                  type="text"
                  name="customerName"
                  value={
                    form.customerName
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter customer name"
                  disabled={
                    submitting
                  }
                  autoFocus
                />

                {errors.customerName ? (
                  <small>
                    {
                      errors.customerName
                    }
                  </small>
                ) : null}
              </div>


              {/* MOBILE */}

              <div
                className="add-customer-field"
              >
                <label>
                  Mobile Number *
                </label>

                <input
                  type="text"
                  name="mobile"
                  value={
                    form.mobile
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter 10-digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={
                    submitting
                  }
                />

                {errors.mobile ? (
                  <small>
                    {
                      errors.mobile
                    }
                  </small>
                ) : null}
              </div>


              {/* EMAIL */}

              <div
                className="add-customer-field"
              >
                <label>
                  Email Address
                </label>

                <input
                  type="email"
                  name="email"
                  value={
                    form.email
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter email address"
                  disabled={
                    submitting
                  }
                />

                {errors.email ? (
                  <small>
                    {
                      errors.email
                    }
                  </small>
                ) : null}
              </div>


              {/* COMPANY */}

              <div
                className="add-customer-field"
              >
                <label>
                  Company Name
                </label>

                <input
                  type="text"
                  name="companyName"
                  value={
                    form.companyName
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter company name"
                  disabled={
                    submitting
                  }
                />
              </div>
            </div>


            {/* =============================================
                ADDRESS
            ============================================= */}

            <div
              className="add-customer-field"
            >
              <label>
                Address
              </label>

              <textarea
                name="address"
                rows={4}
                value={
                  form.address
                }
                onChange={
                  handleChange
                }
                placeholder="Enter customer address"
                disabled={
                  submitting
                }
              />
            </div>
          </div>


          {/* =================================================
              FOOTER
          ================================================= */}

          <div
            className="add-customer-footer"
          >
            <button
              type="button"
              className="add-customer-cancel"
              onClick={
                handleClose
              }
              disabled={
                submitting
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              className="add-customer-save"
              disabled={
                submitting
              }
            >
              <FiPlus />

              {submitting
                ? "Adding..."
                : "Add Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default AddCustomerDialog;